import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  DocBlock,
  PagedDocument,
  Screen,
  parseDocumentHtml,
} from '@/shared/components/ios-screen';
import { LoadingLayout } from '@/shared/components/loading-layout';

export const TOC_DOCUMENT_URL = '/toc.html';

type DocState =
  | { kind: 'loading' }
  | { kind: 'ready'; blocks: DocBlock[] }
  | { kind: 'error' };

/** Loads and parses the static legal document into paginatable blocks. */
export const useLegalDocument = (url = TOC_DOCUMENT_URL) => {
  const [state, setState] = useState<DocState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => {
        if (cancelled) return;
        const blocks = parseDocumentHtml(html);
        setState(blocks.length ? { kind: 'ready', blocks } : { kind: 'error' });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [url, attempt]);

  const retry = useMemo(() => () => setAttempt(a => a + 1), []);
  return { state, retry };
};

/**
 * Terms of service & privacy policy as a bounded, paginated reader. The full
 * legal text is parsed from the static Termly export and split into pages
 * sized to the screen — nothing is removed; Back / Next, swipe, ←/→ and a
 * "Jump to section" picker move through it. In-document links jump to the
 * page holding their target. The user's theme preference is left untouched.
 */
export const Toc: FC = () => {
  const { state, retry } = useLegalDocument();

  useEffect(() => {
    document.title = 'Terms & privacy · Bhabi';
  }, []);

  return (
    <Screen asMain>
      <header className="bh-screen__head">
        <p className="bh-eyebrow">The fine print</p>
        <h1 className="bh-screen__title" style={{ fontSize: '1.35rem' }}>
          Terms of service &amp; privacy policy
        </h1>
      </header>

      {state.kind === 'loading' && (
        <div className="bh-screen__fill" style={{ justifyContent: 'center' }}>
          <LoadingLayout inline hideBrand text="Opening the fine print…" />
        </div>
      )}

      {state.kind === 'error' && (
        <div
          className="bh-screen__fill bh-panel"
          role="alert"
          style={{ alignContent: 'center', justifyItems: 'center' }}
        >
          <p>We couldn&apos;t load the terms right now.</p>
          <div className="bh-row" style={{ justifyContent: 'center' }}>
            <button
              type="button"
              className="bh-btn bh-btn--primary bh-btn--small"
              onClick={retry}
            >
              Try again
            </button>
            <a
              className="bh-btn bh-btn--secondary bh-btn--small"
              href={TOC_DOCUMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open full document
            </a>
          </div>
        </div>
      )}

      {state.kind === 'ready' && (
        <PagedDocument
          className="bh-screen__fill"
          blocks={state.blocks}
          label="Terms of service and privacy policy"
          unit="Page"
          sectionPicker
          fontPx={16}
        />
      )}
    </Screen>
  );
};
